<template>
  <div class="collection-search-add">
    <search
      global
      placeholder="Search Sales History and Ladder cards"
      :source="searchSource"
      :sort="sort"
      v-bind="$attrs"
      @select="handleSelect"
    >
      <template #header>
        <div class="header">
          <pill
            v-for="(option, i) in sourceOptions"
            :key="i"
            :active="option.value === searchSource"
            @click="searchSource = option.value"
          >
            <icon :name="option.icon" />
            <span class="pill-text">{{ option.label }}</span>
          </pill>
        </div>
      </template>

      <template #default="slotProps">
        <card-list-item
          v-if="slotProps.source === 'cards'"
          :card="slotProps.item"
          :fields="['currentValue']"
        />

        <sales-list-item
          v-else-if="slotProps.source === 'salesarchive'"
          split
          :item="slotProps.item"
        >
          <verified-icon v-if="slotProps.item.cardId" />
        </sales-list-item>
      </template>
    </search>

    <div class="other-options">
      <p>OR</p>

      <div class="grid">
        <div>
          <cta-card
            icon="assignment_turned_in"
            title="Enter Graded Card Cert"
            description="Enter the PSA, BGS, SGC or CGC cert from your slab and we will pull all the details from the grading company and fill out your collection card for you."
            @click="showCertInput = !showCertInput"
          />

          <align v-show="showCertInput" class="cert-input" flex>
            <text-input
              v-model="certInput"
              label="Cert #"
              placeholder="63444200"
            />

            <select-input
              v-model="graderInput"
              label="Grader"
              :options="graderOptions"
            />

            <btn
              :loading="addingFromCert"
              loading-text="Submitting"
              @click="addCardFromCert"
            >
              Submit
            </btn>
          </align>
        </div>

        <cta-card
          icon="drive_file_move_rtl"
          title="Cert CSV"
          description="Upload your PSA, BGS, SGC or CGC cards in bulk. We will quickly fetch all these cards from the grading company and get them into your collection."
          @click="addCertUpload"
        />

        <cta-card
          icon="view_carousel"
          title="Add Manually"
          description="Not seeing your card in the search? Don't worry we can help you quickly add the card to your collection and determine the value."
          @click="addCustom"
        />

        <cta-card
          icon="file_upload"
          title="Upload CSV"
          description="Upload to your collection in bulk. We will quickly get all these cards into your collection with estimated values."
          @click="addUpload"
        />
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

import { cardService } from '@/services';
import { GRADING_COMPANY_UPLOAD_OPTIONS } from '@cardladder/shared/constants';
import { applySaleTitleToCollectionCard } from '@cardladder/shared/utils/collectionCardTitle.util';

import CardListItem from '@cardladder/shared/components/CardListItem';
import SalesListItem from '@cardladder/shared/components/SalesListItem';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';
import Search from '@/components/Search';

export default {
  components: {
    CardListItem,
    SalesListItem,
    VerifiedIcon,
    Search,
  },

  data() {
    return {
      searchSource: 'salesarchive',
      sourceOptions: [
        { value: 'salesarchive', label: 'Sales History', icon: 'point_of_sale', sort: 'date' },
        { value: 'cards', label: 'Ladder', icon: 'cl_ladder', sort: 'numSales' },
      ],
      certInput: '',
      graderInput: 'psa',
      graderOptions: GRADING_COMPANY_UPLOAD_OPTIONS,
      showCertInput: false,
      addingFromCert: false,
    };
  },

  computed: {
    sort() {
      return this.sourceOptions.find(({ value }) => value === this.searchSource).sort;
    },
  },

  methods: {
    async handleSelect(item) {
      if (this.searchSource === 'cards') {
        const collectionCard = await this.getCollectionCardFromId(item.id);
        this.$emit('add', collectionCard);
      } else if (this.searchSource === 'salesarchive') {
        this.addCardFromSale(item);
      }
    },

    async getCollectionCardFromId(cardId) {
      const card = await cardService.getCard(cardId);

      return {
        cardId,
        label: card.label,
        image: card.image,
        player: card.player,
        playerIndexId: card.playerIndexId || null,
        set: card.set || '',
        variation: card.variation || '',
        year: card.year || '',
        number: card.number || '',
        category: card.category,
        condition: card.condition,
        pop: card.pop || null,
        currentValue: card.stats?.currentValue || null,
        marketValue: card.stats?.marketValue || null,
        guideValue: card.stats?.guideValue || null,
      };
    },

    async addCardFromSale(sale) {
      try {
        let collectionCard = {};

        if (sale.cardId) {
          collectionCard = await this.getCollectionCardFromId(sale.cardId);
        } else {
          const description = (sale.cardDescription || sale.title || '').trim();

          try {
            const { data: indexResponse } = await firebase.functions().httpsCallable('httpIndexFromDescription')({ description });

            if (indexResponse && indexResponse.id) {
              if (indexResponse.isPlayerIndex) {
                collectionCard.playerIndexId = indexResponse.id;
                collectionCard.player = indexResponse.player || indexResponse.label || collectionCard.player || '';
              } else {
                collectionCard.otherIndexId = indexResponse.id;
              }
            }
          } catch (error) {
            // swallow
          }

          collectionCard.playerIndexPrice = sale.price;
          collectionCard.playerIndexDate = sale.date;

          if (sale.image) {
            collectionCard.imageUrl = sale.image;
          }

          if (sale.cert) {
            collectionCard.cert = sale.cert;
          }

          if (sale.grader) {
            collectionCard.grader = sale.grader;
          }

          applySaleTitleToCollectionCard(collectionCard, sale.cardDescription || sale.title);
        }

        collectionCard.datePurchased = sale.date;
        collectionCard.investment = sale.price;

        this.$emit('add', collectionCard);
      } catch (e) {
        this.$toasted.error('Error adding the card from the sale');
        Sentry.captureException(e);
      }
    },

    async addCardFromCert() {
      this.addingFromCert = true;

      try {
        const { data: collectionCard } = await firebase.functions().httpsCallable('httpbuildcollectioncard')({
          cert: this.certInput,
          grader: this.graderInput,
        });

        if (collectionCard.datePurchased) {
          collectionCard.datePurchased = new Date(collectionCard.datePurchased);
        }

        this.$emit('add', collectionCard);
      } catch (e) {
        this.$toasted.error('No information on this Cert');
        Sentry.captureException(e);
      }

      this.addingFromCert = false;
      this.showCertInput = false;
    },

    addCustom() {
      this.$emit('add', {});
    },

    addUpload() {
      this.$emit('upload');
    },

    addCertUpload() {
      this.$emit('certupload');
    },
  },
};
</script>

<style lang="scss" scoped>
.collection-search-add {
  max-width: 800px;
  margin: 0 auto;
}

.other-options {
  margin-top: $gp;
  text-align: center;

  p {
    font-weight: bold;
    font-size: 18px;
  }
}

.grid {
  display: grid;
  gap: $gp;
  margin: $gp * 2 auto;
  grid-template-columns: repeat(2, 1fr);
  max-width: 800px;
  --icon-size: 40px;
  --text-input-margin-bottom: 0;

  @media($small-down) {
    grid-template-columns: repeat(1, 1fr);
  }
}

.cert-input {
  margin-top: $gp;
  --select-input-margin-bottom: 0;
}

.header {
  margin: $gp * .5 0 0 $gp * .5;
  --icon-size: 18px;
}

.pill-text {
  line-height: 1;
  padding-left: 5px;
}
</style>
